import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, KeyRound, Send, Copy, Trash } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PasswordInput } from "@/components/PasswordInput";

const empty = { email: "", user_id: "", name: "", contact: "", role: "viewer", password: "" };

const roleStyle = {
  admin: "bg-[#0F172A] text-white",
  manager: "bg-[#7C3AED] text-white",
  editor: "bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/30",
  viewer: "bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB]",
};

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "viewer" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const fetchInvites = async () => {
    try {
      const r = await api.get("/users/invites");
      setInvites(r.data);
    } catch {}
  };

  const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteBusy(true);
    try {
      const r = await api.post("/users/invite", inviteForm);
      const link = inviteLink(r.data.token);
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success(`Invite link copied to clipboard. Share it with ${inviteForm.email}.`);
      setInviteForm({ email: "", role: "viewer" });
      setInviteOpen(false);
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invite");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteLink(token));
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy. Long-press to copy manually.");
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite link? The recipient will no longer be able to use it.")) return;
    try {
      await api.delete(`/users/invites/${id}`);
      toast.success("Invite revoked");
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    }
  };

  const fetchAll = async () => {
    const r = await api.get("/users");
    setItems(r.data);
  };
  useEffect(() => { fetchAll(); fetchInvites(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, user_id: u.user_id, name: u.name, contact: u.contact || "", role: u.role, password: "" });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = {
          name: form.name, contact: form.contact, role: form.role,
          user_id: form.user_id,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-10 max-w-6xl" data-testid="users-page">
      <PageHeader overline="Access Control" title="Users & Roles" testid="users-header">
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-user-button" variant="outline"
          className="rounded-none border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white h-10 text-xs uppercase tracking-wider font-semibold">
          <Send className="h-3.5 w-3.5 mr-2" /> Invite by link
        </Button>
        <Button onClick={openNew} data-testid="add-user-button"
          className="rounded-none bg-[#0F172A] hover:bg-[#1E293B] text-white h-10 text-xs uppercase tracking-wider font-semibold">
          + New User
        </Button>
      </PageHeader>

      {/* Pending invites */}
      {invites.filter((i) => !i.used_at).length > 0 && (
        <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-4 mb-6" data-testid="pending-invites-section">
          <div className="overline mb-3">Pending invites — share these links manually</div>
          <div className="space-y-2">
            {invites.filter((i) => !i.used_at).map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 p-2 bg-white border border-[#E5E7EB]" data-testid={`invite-row-${inv.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#111827] truncate">{inv.email}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}</div>
                </div>
                <input
                  readOnly
                  value={inviteLink(inv.token)}
                  data-testid={`invite-link-${inv.id}`}
                  className="font-mono text-[10px] bg-[#F9FAFB] border border-[#E5E7EB] px-2 py-1 w-72 truncate"
                  onClick={(e) => e.target.select()}
                />
                <button onClick={() => copyLink(inv.token)} data-testid={`invite-copy-${inv.id}`}
                  title="Copy link" className="p-1.5 hover:bg-[#E5E7EB]"><Copy className="h-3.5 w-3.5" /></button>
                <button onClick={() => revokeInvite(inv.id)} data-testid={`invite-revoke-${inv.id}`}
                  title="Revoke invite" className="p-1.5 hover:bg-[#EF4444]/10 text-[#EF4444]"><Trash className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-[#E5E7EB] bg-white overflow-x-auto" data-testid="users-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left px-4 py-3 overline">Name</th>
              <th className="text-left px-4 py-3 overline">Email</th>
              <th className="text-left px-4 py-3 overline">User ID</th>
              <th className="text-left px-4 py-3 overline">Contact</th>
              <th className="text-left px-4 py-3 overline">Role</th>
              <th className="px-4 py-3 overline text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-[#6B7280]">No users yet.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-[#111827]">
                  {u.name}
                  {u.id === me?.id && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#6B7280]">(you)</span>}
                </td>
                <td className="px-4 py-3 text-[#4B5563] font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3 text-[#4B5563] font-mono text-xs">{u.user_id || "—"}</td>
                <td className="px-4 py-3 text-[#4B5563]">{u.contact || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${roleStyle[u.role] || roleStyle.viewer}`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                      title="Edit"
                      className="p-1.5 hover:bg-[#E5E7EB]"><Pencil className="h-3.5 w-3.5" /></button>
                    <button
                      onClick={() => onDelete(u)}
                      disabled={u.id === me?.id}
                      data-testid={`delete-user-${u.id}`}
                      title={u.id === me?.id ? "Cannot delete yourself" : "Delete"}
                      className="p-1.5 hover:bg-[#EF4444]/10 text-[#EF4444] disabled:opacity-30 disabled:hover:bg-transparent">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing ? "Edit User" : "New User"}</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              {editing ? "Update user details, role, or reset their password." : "Add a new user to this business workspace with a role and login credentials."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="user-form">
            <div>
              <Label className="overline mb-1.5 block">Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                data-testid="user-name" className="rounded-none h-10" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!editing} required
                data-testid="user-email" className="rounded-none h-10 disabled:opacity-60" />
              {editing && <p className="text-xs text-[#6B7280] mt-1">Email cannot be changed after creation.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline mb-1.5 block">User ID (login)</Label>
                <Input value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required minLength={2}
                  data-testid="user-user-id" placeholder="e.g. asad01" className="rounded-none h-10 font-mono lowercase" />
              </div>
              <div>
                <Label className="overline mb-1.5 block">Contact</Label>
                <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  data-testid="user-contact" placeholder="Phone / extension" className="rounded-none h-10" />
              </div>
            </div>
            <div>
              <Label className="overline mb-1.5 block">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role" className="rounded-none h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="admin">Admin — full access (users, settings, all data)</SelectItem>
                  <SelectItem value="manager">Manager — full access (same as Admin)</SelectItem>
                  <SelectItem value="editor">Editor — add/edit/delete ledger entries only</SelectItem>
                  <SelectItem value="viewer">Viewer — read-only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline mb-1.5 block">
                {editing ? "New password (leave blank to keep)" : "Temporary password"}
              </Label>
              <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editing} minLength={editing ? undefined : 6}
                autoComplete="new-password" testid="user-password"
                placeholder={editing ? "Leave blank to keep current password" : "min 6 characters"}
                className="rounded-none h-10 font-mono" />
              {!editing && <p className="text-xs text-[#6B7280] mt-1 flex items-center gap-1"><KeyRound className="h-3 w-3" /> Share this with the user securely.</p>}
            </div>
            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="user-cancel"
                className="rounded-none h-10 text-xs uppercase tracking-wider">Cancel</Button>
              <Button type="submit" data-testid="user-save"
                className="rounded-none h-10 bg-[#0F172A] hover:bg-[#1E293B] text-xs uppercase tracking-wider">
                {editing ? "Update" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite by link Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Invite by link</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Generate a one-time link with a pre-assigned role. Share it via WhatsApp, email, or Slack — the recipient sets their own password.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInvite} className="space-y-4 mt-2" data-testid="invite-form">
            <div>
              <Label className="overline mb-1.5 block">Email</Label>
              <Input type="email" value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required data-testid="invite-email"
                placeholder="teammate@example.com" className="rounded-none h-10" />
            </div>
            <div>
              <Label className="overline mb-1.5 block">Role</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger data-testid="invite-role" className="rounded-none h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="admin">Admin — full access</SelectItem>
                  <SelectItem value="manager">Manager — full access (same as Admin)</SelectItem>
                  <SelectItem value="editor">Editor — entries only</SelectItem>
                  <SelectItem value="viewer">Viewer — read-only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-[#6B7280]">Link expires in 7 days. The recipient sets name, user ID, and password when they click it.</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteBusy}
                data-testid="invite-cancel" className="rounded-none h-10 text-xs uppercase tracking-wider">Cancel</Button>
              <Button type="submit" disabled={inviteBusy} data-testid="invite-submit"
                className="rounded-none h-10 bg-[#2563EB] hover:bg-[#1E40AF] text-white text-xs uppercase tracking-wider">
                {inviteBusy ? "Creating…" : "Create link"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
